import { useEffect, useRef } from "react"
import { toast } from "sonner"
import type { AirportOption } from "@/types/booking"
import { useBooking } from "@/context/BookingContext"
import { cacheCityCode } from "@/lib/airports"

const CACHE_KEY = "auto-detected-airport"
const PELIKAN_URL = "https://www.pelikan.sk/gf1/pelijee-sk/mobile/autocomplete"
const NOMINATIM_URL = "https://nominatim.openstreetmap.org/reverse"
const GEO_TIMEOUT_MS = 5000

interface NominatimAddress {
  city?: string
  town?: string
  village?: string
  municipality?: string
  county?: string
  state?: string
}

interface NominatimResponse {
  address?: NominatimAddress
}

interface PelikanItem {
  airportCode?: string
  cityCode: string
  city: string
  country?: string
}

function extractCityName(address: NominatimAddress): string | null {
  return address.city || address.town || address.village || address.municipality || null
}

async function reverseGeocode(lat: number, lon: number): Promise<string | null> {
  const url = `${NOMINATIM_URL}?format=json&lat=${lat}&lon=${lon}&zoom=10&addressdetails=1`
  const res = await fetch(url, {
    headers: { "User-Agent": "TurnagoTravel/1.0" },
  })
  const contentType = res.headers.get("content-type") ?? ""
  if (!res.ok || !contentType.includes("application/json")) return null
  const data: NominatimResponse = await res.json()
  if (!data.address) return null
  return extractCityName(data.address)
}

async function findAirportByCity(cityName: string): Promise<AirportOption | null> {
  const url = `${PELIKAN_URL}?q=${encodeURIComponent(cityName)}&lang=en`
  const res = await fetch(url)
  const contentType = res.headers.get("content-type") ?? ""
  if (!res.ok || !contentType.includes("application/json")) return null
  const data: unknown = await res.json()
  if (!Array.isArray(data)) return null

  const cityItem = (data as PelikanItem[]).find(
    (item) => !item.airportCode && item.cityCode && item.city
  )

  if (!cityItem) return null

  return {
    code: cityItem.cityCode,
    city: cityItem.city,
    country: cityItem.country ?? "",
  }
}

function loadCachedAirport(): AirportOption | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as AirportOption
  } catch {
    return null
  }
}

function cacheAirport(airport: AirportOption) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(airport))
  } catch {}
}

export function useAutoDetectAirport(enabled: boolean) {
  const { state, dispatch } = useBooking()
  const ranRef = useRef(false)

  useEffect(() => {
    if (!enabled || ranRef.current) return
    ranRef.current = true

    const cached = loadCachedAirport()
    if (cached) {
      if (!state.departureAirports.some((a) => a.code === cached.code)) {
        dispatch({ type: "ADD_DEPARTURE_AIRPORT", airport: cached })
        cacheCityCode(cached.code, cached.city, cached.country)
        toast.info("Departure city auto-detected from your location", { duration: 4000 })
      }
      return
    }

    if (!navigator.geolocation) return

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords
          const cityName = await reverseGeocode(latitude, longitude)
          if (!cityName) return

          const airport = await findAirportByCity(cityName)
          if (!airport) return

          if (!state.departureAirports.some((a) => a.code === airport.code)) {
            dispatch({ type: "ADD_DEPARTURE_AIRPORT", airport })
            cacheCityCode(airport.code, airport.city, airport.country)
            cacheAirport(airport)
            toast.info("Departure city auto-detected from your location", { duration: 4000 })
          }
        } catch {}
      },
      () => {},
      { timeout: GEO_TIMEOUT_MS }
    )
  }, [enabled])
}
